import { defineStore, acceptHMRUpdate } from 'pinia';
import { ref, reactive, computed } from 'vue';
import { logger } from 'data-layer/exports';
import { useYoutubeApi } from '#imports';

interface PlayerState {
  loading: boolean;
  error: string | null;
  duration?: number;
}

export const useYouTubePlayerStore = defineStore('YouTubePlayerStore', () => {
  const lastPlayerChanged = ref<string | null>(null);
  const playerStates = reactive<Record<string, PlayerState>>({});
  const youtubeApiReady = ref(false);
  const apiLoading = ref(false);
  const apiError = ref<string | null>(null);

  // Non-serializable state
  const players: Record<string, HTMLElement> = {};
  const initializedPlayers = ref<Record<string, boolean>>({});

  const isInitialized = computed(
    () => (videoId: string) => !!initializedPlayers.value[videoId],
  );

  const getPlayerState = computed(
    () =>
      (videoId: string): PlayerState =>
        playerStates[videoId] ?? { loading: false, error: null },
  );

  async function ensureYoutubeApiReady() {
    if (youtubeApiReady.value) return;

    apiLoading.value = true;
    apiError.value = null;

    try {
      await useYoutubeApi();
      youtubeApiReady.value = true;
    } catch (err) {
      logger.error('[YouTube] API failed to load', err);
      apiError.value = 'Failed to load YouTube API';
    } finally {
      apiLoading.value = false;
    }
  }

  function setPlayerLoading(
    videoId: string,
    loading: boolean,
    error: string | null = null,
  ) {
    playerStates[videoId] = { loading, error };
  }

  function setPlayerDuration(videoId: string, duration: number) {
    if (!playerStates[videoId]) {
      playerStates[videoId] = { loading: false, error: null };
    }
    playerStates[videoId].duration = duration;
  }

  function registerPlayer(videoId: string, el: HTMLElement) {
    players[videoId] = el;
    initializedPlayers.value[videoId] = true;
  }

  function pausePlayer(videoId: string) {
    const el = players[videoId];
    if (!el) return;
    const iframe = el.querySelector('iframe') as HTMLIFrameElement;
    iframe?.contentWindow?.postMessage(
      '{"event":"command","func":"pauseVideo","args":""}',
      '*',
    );
  }

  function handleVideoChange(newPlayerId: string) {
    if (lastPlayerChanged.value && lastPlayerChanged.value !== newPlayerId) {
      pausePlayer(lastPlayerChanged.value);
    }
    lastPlayerChanged.value = newPlayerId;
  }

  function setBestThumbnail(videoId: string) {
    const el = players[videoId];
    if (!el) return;

    const img = el.querySelector('.videoPlayer__inner') as HTMLElement | null;
    if (!img) return;

    const sizes = [
      'maxresdefault',
      'sddefault',
      'hqdefault',
      'mqdefault',
      'default',
    ];

    (function tryNext(i: number): void {
      if (i >= sizes.length) return;

      const url = `https://img.youtube.com/vi/${videoId}/${sizes[i]}.jpg`;
      const tester = new Image();

      tester.onload = () => {
        if (tester.naturalWidth === 120 && tester.naturalHeight === 90) {
          tryNext(i + 1);
        } else {
          img.style.backgroundImage = `url(${url})`;
        }
      };

      tester.onerror = () => {
        tryNext(i + 1);
      };

      tester.src = url;
    })(0);
  }

  function cleanupPlayers() {
    for (const videoId in players) {
      try {
        const el = players[videoId];
        const iframe = el.querySelector('iframe');
        iframe?.remove();
      } catch (e) {
        logger.error('Error cleaning up player', e);
      }
      delete players[videoId];
    }
    initializedPlayers.value = {};
    // Clear reactive states too
    Object.keys(playerStates).forEach((key) => delete playerStates[key]);
    lastPlayerChanged.value = null;
  }

  async function createAndPlay(
    videoId: string,
    container: HTMLElement,
    timestamp = 0,
  ) {
    try {
      const YT = await useYoutubeApi();

      setPlayerLoading(videoId, true);

      container.classList.remove('videoPlayer--isOverlay');
      container.innerHTML = '';

      const playerDiv = document.createElement('div');
      playerDiv.id = videoId;
      container.appendChild(playerDiv);

      const ytPlayer = new YT.Player(playerDiv.id, {
        height: '100%',
        width: '100%',
        videoId,
        playerVars: { rel: 0, start: timestamp },
        events: {
          onReady: (event: any) => {
            setPlayerLoading(videoId, false);
            setPlayerDuration(videoId, event.target.getDuration());
            event.target.playVideo();
            handleVideoChange(videoId);
          },
          onError: () => {
            setPlayerLoading(videoId, false, 'Error loading video');
          },
          onStateChange: (event: any) => {
            if (event.data === YT.PlayerState.PLAYING) {
              handleVideoChange(videoId);
            }
          },
        },
      });

      registerPlayer(videoId, container);
    } catch (err) {
      logger.error(`YouTube player init failed for ${videoId}`, err);
      setPlayerLoading(videoId, false, 'Failed to load YouTube API');
    }
  }

  return {
    lastPlayerChanged,
    playerStates,
    youtubeApiReady,
    apiLoading,
    apiError,
    isInitialized,
    getPlayerState,
    ensureYoutubeApiReady,
    setPlayerLoading,
    setPlayerDuration,
    registerPlayer,
    pausePlayer,
    handleVideoChange,
    setBestThumbnail,
    cleanupPlayers,
    createAndPlay,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useYouTubePlayerStore, import.meta.hot),
  );
}
